import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { createCrudHooks } from 'src/shared/hooks/createCrudHooks';
import { clientsApi } from './clientsApi';
import type {
  ClientListParams,
  ClientNotePayload,
  ClientPayload,
  ClientUpdatePayload,
} from './types';
import type { Client } from './types';

const KEY = 'clients';

// Inicio reads clients too: new profiles today, and whose birthday is coming.
const INSIGHTS_KEY = ['insights'];

const hooks = createCrudHooks<Client, ClientListParams, ClientPayload, ClientUpdatePayload, number>(
  KEY,
  clientsApi,
  { alsoInvalidate: [INSIGHTS_KEY] }
);

export const useClients = hooks.useList;
export const useCreateClient = hooks.useCreate;
export const useUpdateClient = hooks.useUpdate;
export const useDeleteClient = hooks.useDelete;

/** One client's full profile, including her aggregates. */
export const useClient = (id: number) =>
  useQuery({
    queryKey: [KEY, 'detail', id],
    queryFn: () => clientsApi.get(id),
    enabled: Number.isFinite(id) && id > 0,
  });

export const useClientNotes = (clientId: number) =>
  useQuery({
    queryKey: [KEY, 'notes', clientId],
    queryFn: () => clientsApi.notes(clientId),
    enabled: Number.isFinite(clientId) && clientId > 0,
  });

export const useAddClientNote = (clientId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ClientNotePayload) => clientsApi.addNote(clientId, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, 'notes', clientId] }),
  });
};

export const useMergeClients = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, targetId }: { id: number; targetId: number }) =>
      clientsApi.merge(id, targetId),
    // A merge moves notes and retires a profile, so nothing cached about
    // clients survives it intact.
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: [KEY] }),
        qc.invalidateQueries({ queryKey: INSIGHTS_KEY }),
      ]),
  });
};
